import { put, call, takeLatest, delay } from 'redux-saga/effects';
import { showErrorToast } from '../actions/toast';
import ActionTypes from '../constants/ActionTypes';
import { getApiErrorData } from '../utils/helperFunction';
import { GET_SESSION_RETRY_COUNT } from '../constants/LiveKit';
import {
  getDhwaniInterviewSession,
  validateDhwaniInterviewSession,
  uploadCandidateCV,
  cvQuestionGenerationStatus,
  cvUploadStatus,
} from '../api/aiInterview';
import { errorLogger } from '../utils/trackingUtil';
import { isRetryableNetworkError } from './utils';

function* getInterviewSessionSaga(action) {
  const maxRetries = GET_SESSION_RETRY_COUNT;
  let attempts = 0;

  while (attempts < maxRetries) {
    try {
      const response = yield call(getDhwaniInterviewSession, action.payload);
      yield put({
        type: ActionTypes.GET_INTERVIEW_SESSION_SUCCESS,
        payload: response.data.data,
      });
      yield put({
        type: ActionTypes.VALIDATE_DHWANI_INTERVIEW_SESSION_RESET,
      });

      return; // exit saga
    } catch (error) {
      attempts += 1;

      // If NOT retryable, exit normally
      if (!isRetryableNetworkError(error)) {
        yield put({
          type: ActionTypes.GET_INTERVIEW_SESSION_FAIL,
          error: error.message,
        });
        const errorInfo = getApiErrorData(error, 'INS');
        yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
        errorLogger(error);
        return;
      }

      yield delay(5000);
    }
  }

  // Retries exceeded
  const finalError = new Error('Max retries reached. Network unavailable.');
  yield put({
    type: ActionTypes.GET_INTERVIEW_SESSION_FAIL,
    error: finalError.message,
  });

  yield put(showErrorToast('Network unavailable. Please try again later.'));
  errorLogger(finalError);
}

function* validateDhwaniInterviewSessionSaga(args) {
  if (args.payload && args.payload.reset) {
    // Reset the reducer state only, no API call
    yield put({
      type: ActionTypes.VALIDATE_DHWANI_INTERVIEW_SESSION,
    });
    return;
  }
  try {
    const response = yield call(validateDhwaniInterviewSession, args.payload);
    yield put({
      type: ActionTypes.VALIDATE_DHWANI_INTERVIEW_SESSION_SUCCESS,
      payload: response.data.data,
    });
    // No return needed, reducer will update state
  } catch (error) {
    yield put({
      type: ActionTypes.VALIDATE_DHWANI_INTERVIEW_SESSION_FAIL,
      error: error.message,
    });
    const errorInfo = getApiErrorData(error, 'ISV');
    yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    errorLogger(error);
  }
}

function* uploadCandidateCvSaga(args) {
  try {
    const payload = new FormData();
    payload.append('file', args.payload.file);
    payload.append('upload_type', 'generic');
    payload.append('filename_prefix', 'AI_CV_');
    payload.append('name', args.payload.file.name);
    payload.append('source', 'aiinterviewcvupload');
    payload.append('file_size', args.payload.file.size);
    payload.append('test_slug', args.payload.testSlug);
    payload.append('test_solution_id', args.payload.testSolutionId);
    const response = yield call(uploadCandidateCV, payload);
    yield put({
      type: ActionTypes.UPLOAD_CANDIDATE_CV_SUCCESS,
      payload: response.data.data,
    });
    // No return needed, reducer will update state
  } catch (error) {
    yield put({
      type: ActionTypes.UPLOAD_CANDIDATE_CV_FAIL,
      error: error.message,
    });
    const errorInfo = getApiErrorData(error, 'CVU');
    yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    errorLogger(error);
  }
}

function* CheckCvGenerationStatus(args) {
  try {
    const response = yield call(cvQuestionGenerationStatus, args.payload);
    if (response.data.data.is_ready) {
      yield put({
        type: ActionTypes.CV_GENERATION_STATUS_SUCCESS,
        payload: response.data.data.is_ready,
      });
    }
    // No return needed, reducer will update state
  } catch (error) {
    yield put({
      type: ActionTypes.CV_GENERATION_STATUS_FAIL,
      payload: response.data.data,
    });
    const errorInfo = getApiErrorData(error, 'CVG');
    yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    errorLogger(error);
  }
}

function* CheckCvUploadStatus(args) {
  try {
    const response = yield call(cvUploadStatus, args.payload);
    yield put({
      type: ActionTypes.CV_UPLOAD_STATUS_SUCCESS,
      payload: response.data.data,
    });
    // No return needed, reducer will update state
  } catch (error) {
    yield put({
      type: ActionTypes.CV_UPLOAD_STATUS_FAIL,
    });
    const errorInfo = getApiErrorData(error, 'CSU');
    yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    errorLogger(error);
  }
}

export default function* loadAiInterviewSaga() {
  yield takeLatest(ActionTypes.GET_INTERVIEW_SESSION, getInterviewSessionSaga);
  yield takeLatest(
    ActionTypes.VALIDATE_DHWANI_INTERVIEW_SESSION,
    validateDhwaniInterviewSessionSaga
  );
  yield takeLatest(ActionTypes.UPLOAD_CANDIDATE_CV, uploadCandidateCvSaga);
  yield takeLatest(ActionTypes.CV_GENERATION_STATUS, CheckCvGenerationStatus);
  yield takeLatest(ActionTypes.CV_UPLOAD_STATUS, CheckCvUploadStatus);
}
